// src/components/RolePickerModal.jsx
// Post-login role selection modal. No props — event-driven.
// Triggered by 'open-role-picker' custom event fired from LoginModal.js.
//
// Shows for new signups (isNewSignup: true in event detail) always.
// Shows for existing users only if their roles === ['player'] (default) AND not dismissed this session.
// Calls POST /api/set-roles, gets back a fresh JWT, updates AuthContext immediately.
//
// FIX: replaced IonModal/IonContent with plain div — IonModal requires full Ionic
//      initialisation on web and renders blank without it. Plain div works on both web + Capacitor.

import { useState, useEffect } from "react";
import { useAuth } from "../Context/AuthContext";
import { apiFetch } from "../utils/api";

const ROLES = [
  {
    id: "player",
    label: "Player",
    icon: "\uD83C\uDFC3",
    desc: "Join games, track stats, and compete in leagues.",
    alwaysOn: true,
  },
  {
    id: "coach",
    label: "Coach",
    icon: "\uD83D\uDCCB",
    desc: "Manage squads, schedule matches, and view team insights.",
    minorDisabled: true,
  },
  {
    id: "guardian",
    label: "Guardian",
    icon: "\uD83D\uDEE1\uFE0F",
    desc: "Monitor a young player's activity and receive safety updates.",
  },
];

export default function RolePickerModal() {
  const { user, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(["player"]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(function () {
    function handleOpen(e) {
      var detail = e.detail || {};
      var eventUser = detail.user || user;
      var isNewSignup = detail.isNewSignup === true;

      var roles = eventUser && Array.isArray(eventUser.roles) ? eventUser.roles : ["player"];
      var isDefaultOnly = roles.length === 1 && roles[0] === "player";

      var alreadyPicked = false;
      try { alreadyPicked = localStorage.getItem("gt_role_picked") === "1"; } catch (ex) {}

      // Only show if new signup OR (default-only roles AND not already dismissed this session)
      if (!isNewSignup && (!isDefaultOnly || alreadyPicked)) return;

      var initialSelected = [];
      for (var i = 0; i < roles.length; i++) initialSelected.push(roles[i]);
      setSelected(initialSelected);
      setError("");
      setOpen(true);
    }

    window.addEventListener("open-role-picker", handleOpen);
    return function () { window.removeEventListener("open-role-picker", handleOpen); };
  }, [user]);

  function toggleRole(roleId) {
    setSelected(function (prev) {
      if (roleId === "player") return prev; // player is always on
      if (prev.includes(roleId)) {
        return prev.filter(function (r) { return r !== roleId; });
      }
      var next = [];
      for (var i = 0; i < prev.length; i++) next.push(prev[i]);
      next.push(roleId);
      return next;
    });
  }

  function dismiss() {
    try { localStorage.setItem("gt_role_picked", "1"); } catch (ex) {}
    setOpen(false);
  }

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      var token = "";
      try {
        token = sessionStorage.getItem("gt_token")
             || localStorage.getItem("gt_token")
             || localStorage.getItem("token")
             || "";
      } catch (ex) {}
      var res = await apiFetch("/api/set-roles", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify({ roles: selected }),
      });
      var data = await res.json();
      if (data.ok && data.token) {
        login(data.token, data.user);
        try { localStorage.setItem("gt_role_picked", "1"); } catch (ex) {}
        setOpen(false);
      } else {
        setError(data.error || "Could not save roles — try again.");
      }
    } catch (ex) {
      setError("Network error — please check your connection.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  var isMinor = user && user.is_minor;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      onClick={function(e) { if (e.target === e.currentTarget) dismiss(); }}>
      <div className="absolute inset-0 bg-black/80" onClick={dismiss} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-t-2xl sm:rounded-2xl w-full max-w-sm mx-0 sm:mx-4 p-6 pb-10 sm:pb-6 z-10 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="mb-5">
          <p className="text-lime-400 text-xs font-mono uppercase tracking-widest mb-1">
            You&apos;re in
          </p>
          <h2 className="text-white text-lg font-semibold tracking-tight leading-snug">
            How are you using Gametrait?
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Pick all that apply. You can change this any time.
          </p>
        </div>

        {/* Role cards */}
        <div className="space-y-2 mb-5">
          {ROLES.map(function (role) {
            var disabled = !!(role.minorDisabled && isMinor);
            var active = selected.includes(role.id);
            var alwaysOn = !!role.alwaysOn;

            var cardCls = "w-full flex items-start gap-3 px-4 py-3 rounded-xl border text-left transition-all ";
            if (disabled) {
              cardCls += "border-zinc-800 bg-zinc-800/30 opacity-40 cursor-not-allowed";
            } else if (alwaysOn) {
              cardCls += "border-lime-400/40 bg-lime-400/5 cursor-default";
            } else if (active) {
              cardCls += "border-lime-400 bg-lime-400/10 cursor-pointer";
            } else {
              cardCls += "border-zinc-700 bg-zinc-800/50 hover:border-zinc-500 cursor-pointer";
            }

            return (
              <button
                key={role.id}
                type="button"
                onClick={function () { if (!disabled && !alwaysOn) toggleRole(role.id); }}
                disabled={disabled}
                className={cardCls}
              >
                <span className="text-xl mt-0.5 flex-shrink-0">{role.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={"text-sm font-semibold " + (active ? "text-white" : "text-zinc-300")}>
                      {role.label}
                    </span>
                    {alwaysOn && (
                      <span className="text-lime-400 text-xs font-mono leading-none">always on</span>
                    )}
                    {disabled && (
                      <span className="text-zinc-600 text-xs">not available for minors</span>
                    )}
                  </div>
                  <p className="text-zinc-500 text-xs mt-0.5 leading-relaxed">{role.desc}</p>
                </div>
                {/* Checkbox indicator */}
                {!disabled && (
                  <div
                    className={
                      "w-5 h-5 rounded flex-shrink-0 mt-0.5 flex items-center justify-center border transition-colors " +
                      (active ? "bg-lime-400 border-lime-400" : "border-zinc-600 bg-transparent")
                    }
                  >
                    {active && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="black" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <p className="text-red-400 text-xs mb-3">{error}</p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black font-semibold rounded-xl py-2.5 text-sm transition-colors"
          >
            {saving ? "Saving\u2026" : "Save & continue"}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="w-full text-zinc-500 hover:text-zinc-300 text-sm py-2 transition-colors"
          >
            Skip for now
          </button>
        </div>

      </div>
    </div>
  );
}
